import * as React from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { QColumnCustomizer } from './QColumnCustomizer';
import type { QColumnDef, QColumnConfig, QQuickFilter } from './types';
import { normalizeQuickFilterOptions } from './types';

interface QToolbarProps<T> {
  // Search
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // Quick Filters
  quickFilters?: QQuickFilter[];
  quickFilterValues?: Record<string, string[]>;
  onQuickFilterChange?: (key: string, values: string[]) => void;

  // Advanced Filters
  hasFilters: boolean;
  activeFilterCount: number;
  onOpenFilters: () => void;

  // Column customizer
  columns: QColumnDef<T>[];
  columnConfigs: QColumnConfig[];
  onColumnConfigsChange: (configs: QColumnConfig[]) => void;

  // Header
  headerActions?: React.ReactNode;

  // Total
  totalItems: number;
  itemLabel?: string;
}

function QToolbar<T>({
  search,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  quickFilters,
  quickFilterValues = {},
  onQuickFilterChange,
  hasFilters,
  activeFilterCount,
  onOpenFilters,
  columns,
  columnConfigs,
  onColumnConfigsChange,
  headerActions,
  totalItems,
  itemLabel = 'resultados',
}: QToolbarProps<T>) {
  const [localSearch, setLocalSearch] = React.useState(search ?? '');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    setLocalSearch(search ?? '');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [search]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 300);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="space-y-3 px-4 pt-4">
      {/* Row 1: Search + Actions */}
      <div className="flex items-center gap-2">
        {onSearchChange && (
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-8 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder={searchPlaceholder}
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {localSearch && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => handleSearchChange('')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {hasFilters && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={onOpenFilters}>
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Filtros</span>
              {activeFilterCount > 0 && (
                <Badge variant="default" className="h-5 px-1.5 text-xs rounded-full">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          )}

          <QColumnCustomizer
            columns={columns}
            configs={columnConfigs}
            onChange={onColumnConfigsChange}
          />

          {headerActions}
        </div>
      </div>

      {/* Row 2: Quick Filters + Total count */}
      {(quickFilters?.length || totalItems > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          {quickFilters?.map((qf) => {
            const options = normalizeQuickFilterOptions(qf.options);
            const activeValues = quickFilterValues[qf.key] ?? [];

            return (
              <React.Fragment key={qf.key}>
                {qf.label && (
                  <span className="text-xs text-muted-foreground font-medium">{qf.label}:</span>
                )}
                {options.map((opt) => {
                  const isActive = activeValues.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium transition-colors border',
                        isActive
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-input hover:bg-muted hover:text-foreground'
                      )}
                      style={
                        isActive && opt.color
                          ? { backgroundColor: opt.color, borderColor: opt.color }
                          : undefined
                      }
                      onClick={() => {
                        if (!onQuickFilterChange) return;
                        const next = isActive
                          ? activeValues.filter((v) => v !== opt.value)
                          : [...activeValues, opt.value];
                        onQuickFilterChange(qf.key, next);
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}

          <span className="ml-auto text-xs text-muted-foreground">
            {totalItems.toLocaleString('es-MX')} {itemLabel}
          </span>
        </div>
      )}
    </div>
  );
}

export { QToolbar };
